import { useEffect, useState } from "react";
import { Client } from "../entities/interfaces/client";
import { Selection } from "../components/Orders/ServiceSelector";
import { PaymentMode } from "../entities/dto/order.dto";
import {
  Amount,
  Bill,
  FieldTypeMap,
  Order,
  OrderType,
  PaymentStatus,
} from "../entities/interfaces/order";
import OrderService from "../services/orders.service";
import toast from "react-hot-toast";
import useOrderItem, { ServiceOrder } from "./useOrderItem";
import usePineLabs from "./usePineLabs";
import { TAX_PERCENT } from "../constants";
const useOrder = () => {
  const {
    orderItems,
    addOrUpdateServiceOrderItem,
    addOrUpdateMembershipItem,
    removeOrderItem,
  } = useOrderItem();
  const { createUpiTransaction, createCardTransation } = usePineLabs();
  const [client, setClient] = useState<Client>();
  const [services, setServices] = useState<Selection[]>([]);
  const [membership, setMembership] = useState<boolean>(false);
  const [includeTax, setIncludeTax] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [paymentMode, setPaymentMode] = useState<PaymentMode>();
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(
    PaymentStatus.PENDING
  );
  const [order, setOrder] = useState<Order>();
  const [bill, setBill] = useState<Bill>();
  const [amount, setAmount] = useState<Amount>({
    subtotal: 0,
    tax: 0,
    membershipDiscount: 0,
    total: 0,
  });
  const [billingDate, setBillingDate] = useState<string | undefined>();

  useEffect(() => {
    calculateTotalAmount();
  }, [orderItems, includeTax, client]);

  useEffect(() => {
    updateServices(services);
    calculateTotalAmount();
  }, [client]);

  // Update services state and also update the service order in order items
  const updateServices = (services: Selection[]) => {
    setServices(services);
    if (services?.length === 0) {
      removeOrderItem(OrderType.SERVICE_ORDER);
      return;
    }
    const [price, memberPrice] = calculateServiceAmount(services);
    addOrUpdateServiceOrderItem({
      serviceItems: services.map((s) => ({
        serviceId: s.id,
        quantity: s.qty,
        additionalCharges: s.additionalCharges,
        staffId: s.staffId,
      })),
      // TODO: Calculate amount
      amount: price,
      membershipDiscount: client?.membership?.isActive
        ? price - memberPrice
        : 0,
    });
  };

  // Update membership state and also update the membership order item
  const updateMembership = (membership: boolean) => {
    // TODO: Update membership amount
    if (!membership) {
      removeOrderItem(OrderType.MEMBERSHIP);
      return;
    }
    addOrUpdateMembershipItem({ amount: 200 });
    setMembership(membership);
  };

  // Calculate Service Order Amount
  const calculateServiceAmount = (serviceItems: Selection[]) =>
    serviceItems.reduce(
      (acc, item) => {
        const price = item.storeServices[0]?.price || 0;
        const memberPrice = item.storeServices[0]?.memberPrice || 0;
        const additional = item.additionalCharges || 0;
        const qty = item.qty || 1;
        return [
          acc[0] + (price * qty + additional),
          acc[1] + (memberPrice * qty + additional),
        ];
      },
      [0, 0]
    );

  const calculateTotalAmount = () => {
    const subtotal = orderItems.reduce(
      (acc, item) => acc + (item?.amount || 0),
      0
    );
    const tax = includeTax
      ? Math.round(subtotal * (TAX_PERCENT / 100) * 100) / 100
      : 0;
    const serviceOrder = orderItems.find(
      (item) => item.type === OrderType.SERVICE_ORDER
    ) as ServiceOrder | undefined;
    const membershipDiscount = serviceOrder
      ? serviceOrder?.membershipDiscount || 0
      : 0;
    const total = subtotal + tax - membershipDiscount;

    setAmount({
      subtotal,
      tax,
      membershipDiscount,
      total,
    });
  };

  const validate = () => {
    if (membership && client && paymentMode) {
      return true;
    } else if (client && services.length && paymentMode) {
      return true;
    } else if (client && services.length && membership && paymentMode) {
      return true;
    } else return false;
  };

  const createOrder = async (clientId: string) => {
    const res = await OrderService.create({
      customerId: clientId,
      items: orderItems,
      date: billingDate,
    });
    if (res?.success) {
      setOrder(res.data);
    }
    return res;
  };

  const createBill = async (
    orderId: string,
    paymentMode: PaymentMode,
    includeTax: boolean
  ) => {
    return await OrderService.createBill({
      orderId,
      paymentMode,
      includeTax,
    });
  };

  const updatePayment = async (status: boolean, paymentMode: PaymentMode) => {
    if (!bill) return;
    const res = await OrderService.updatePayment(bill.id, {
      paid: status,
      paymentMode,
    });
    if (res.success) {
      toast.success("Payment updated");
    }
  };

  const initiateOrder = async () => {
    if (!client || !paymentMode) return;
    setLoading(true);
    const order = await createOrder(client.id);
    if (order?.success) {
      const bill = await createBill(order.data.id, paymentMode, includeTax);
      if (bill?.success) {
        // Check for errors
        setBill(bill.data);
        await initiateTransaction(bill.data);
      }
    }
    setLoading(false);
  };

  const createManualOrder = async () => {
    if (!client || !paymentMode) return;
    setLoading(true);
    const order = await createOrder(client.id);
    if (order?.success) {
      const bill = await createBill(order.data.id, paymentMode, includeTax);
      if (bill?.success) {
        // Check for errors
        setBill(bill.data);
      }
    }
    setLoading(false);
    return order?.success && order.data;
  };

  const initiateTransaction = async (bill: Bill) => {
    setLoading(true);
    const paymentResponse = await requestPaymentToPos(
      bill.totalAmount,
      bill.invoidId
    );

    if (paymentResponse?.approvalCode && paymentMode) {
      await OrderService.updatePayment(bill.id, {
        paid: true,
        paymentMode,
      });
      setPaymentStatus(PaymentStatus.SUCCESS);
    } else {
      toast.error("Payment not captured");
      setPaymentStatus(PaymentStatus.FAILED);
    }
    setLoading(false);
  };

  const requestPaymentToPos = async (amount: number, transactionid: string) => {
    const parsedAmt = Math.round(amount * 100);
    if (paymentMode === PaymentMode.UPI) {
      return await createUpiTransaction(`${parsedAmt}`, transactionid);
    } else if (paymentMode === PaymentMode.CARD) {
      return await createCardTransation(`${parsedAmt}`, transactionid);
    }
  };

  const retryPayment = async () => {
    if (bill) {
      await initiateTransaction(bill);
    }
  };

  const makeManualPayment = async () => {
    if (!paymentMode) return;
    await updatePayment(true, paymentMode);
  };

  const printBill = () => {};

  // Generic field setter
  const setters: {
    [K in keyof FieldTypeMap]: (value: FieldTypeMap[K]) => void;
  } = {
    client: setClient,
    services: updateServices,
    membership: updateMembership,
    paymentMode: setPaymentMode,
    paymentStatus: setPaymentStatus,
    bill: setBill,
    includeTax: setIncludeTax,
    billingDate: setBillingDate,
  };

  const updateField = <K extends keyof FieldTypeMap>(
    key: K,
    value: FieldTypeMap[K]
  ) => {
    setters[key](value);
  };

  const clear = () => {
    setClient(undefined);
    updateServices([]);
    updateMembership(false);
    setPaymentMode(undefined);
    setPaymentStatus(PaymentStatus.PENDING);
    setIncludeTax(false);
    setServices([]);
  };

  return {
    client,
    services,
    membership,
    paymentMode,
    paymentStatus,
    bill,
    amount,
    orderItems,
    includeTax,
    loading,
    order,
    updateField,
    // setOrderItem,
    validate,
    clear,
    initiateOrder,
    retryPayment,
    updatePayment,
    makeManualPayment,
    createManualOrder,
  };
};

export default useOrder;
